/* TheSpidy — progressive enhancements.
   Everything here is optional: pages render and purchase without it (forms post natively). */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Cart helper: add one or more lines, then refresh Dawn's drawer ---------- */
  async function addLines(items, trigger) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.setActiveElement(trigger || document.activeElement);
    const res = await fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
    if (drawer) {
      // Refresh the drawer via the Section Rendering API (same HTML Dawn's own add-to-cart uses).
      const ids = drawer.getSectionsToRender().map((s) => s.id).join(',');
      const sep = window.location.search ? '&' : '?';
      const sres = await fetch(`${window.location.pathname}${window.location.search}${sep}sections=${ids}`);
      data.sections = await sres.json();
    }
    if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'spidy', cartData: data });
    }
    if (drawer) {
      drawer.classList.remove('is-empty');
      drawer.renderContents(data);
    } else {
      window.location.href = window.routes.cart_url;
    }
    return data;
  }

  function setLoading(btn, loading) {
    if (!btn) return;
    btn.classList.toggle('is-loading', loading);
    btn.setAttribute('aria-disabled', loading ? 'true' : 'false');
    if (loading) btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
  }

  /* Any form with [data-sp-add] adds the lines described by its inputs:
     items[N][id] / items[N][quantity], or a single name="id" (+ optional quantity).
     Uses delegation so it keeps working after the cart drawer re-renders. */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-sp-add]');
    if (!form) return;
    event.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    if (btn && btn.getAttribute('aria-disabled') === 'true') return;
    const errorEl = form.querySelector('[data-sp-error]');
    if (errorEl) errorEl.hidden = true;

    const fd = new FormData(form);
    const items = [];
    const indexed = {};
    for (const [key, value] of fd.entries()) {
      const m = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
      if (m) (indexed[m[1]] = indexed[m[1]] || {})[m[2]] = Number(value);
    }
    Object.values(indexed).forEach((line) => line.id && items.push({ id: line.id, quantity: line.quantity || 1 }));
    if (!items.length && fd.get('id')) items.push({ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) });
    if (!items.length) return;

    setLoading(btn, true);
    try {
      await addLines(items, btn);
    } catch (err) {
      if (errorEl) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
      }
    } finally {
      setLoading(btn, false);
    }
  });

  /* Cart add-on variant select keeps the hidden id in sync */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-sp-variant-select]');
    if (!select) return;
    const form = select.closest('form');
    const idInput = form && form.querySelector('input[name="id"]');
    if (idInput) idInput.value = select.value;
  });

  /* ---------- Set picker (PDP): radios are the product form's quantity ---------- */
  /* Mix-and-match unit choosers: values per option index, from dropdowns or colour radios. */
  function slotValues(slot) {
    const values = [];
    slot.querySelectorAll('[data-sp-opt]').forEach((el) => {
      const index = Number(el.dataset.spOpt);
      if (el.tagName === 'SELECT') values[index] = el.value;
      else if (el.checked) values[index] = el.value;
    });
    return values;
  }
  function setSlotValues(slot, options) {
    slot.querySelectorAll('[data-sp-opt]').forEach((el) => {
      const value = options[Number(el.dataset.spOpt)];
      if (value === undefined) return;
      if (el.tagName === 'SELECT') el.value = value;
      else el.checked = el.value === value;
    });
    const name = slot.querySelector('[data-sp-colour-name]');
    const colour = slot.querySelector('.sp-colour-pick__input:checked');
    if (name && colour) name.textContent = colour.value;
  }

  class SpSetPicker extends HTMLElement {
    connectedCallback() {
      this.slotsWrap = this.querySelector('[data-sp-slots-wrap]');
      this.addEventListener('change', (e) => {
        if (e.target.name === 'quantity') this.sync();
        else if (e.target.matches && e.target.matches('[data-sp-opt]')) {
          const slot = e.target.closest('[data-sp-slot]');
          if (slot) setSlotValues(slot, slotValues(slot));
          if (slot && slot.dataset.spSlot === '1') this.pushToMainPicker(slotValues(slot));
          this.recalc();
        }
      });
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const html = event.data && event.data.html;
          if (html) {
            this.querySelectorAll('.sp-set-option__price[id]').forEach((el) => {
              const fresh = html.getElementById(el.id);
              if (fresh) el.innerHTML = fresh.innerHTML;
            });
          }
          // Keep the first style chooser in step with the main style picker.
          const variant = event.data && event.data.variant;
          const first = variant && this.querySelector('[data-sp-slot="1"] input[value="' + variant.id + '"]');
          if (first && !first.disabled) first.checked = true;
          if (variant && Array.isArray(variant.options)) {
            const firstSlot = this.querySelector('[data-sp-slot="1"]');
            if (firstSlot) setSlotValues(firstSlot, variant.options);
          }
          this.sync();
        });
      }
      // ?bundle=N (from offer cards) pre-selects that quantity.
      const wanted = new URLSearchParams(window.location.search).get('bundle');
      const wantedInput = wanted && this.querySelector('input[name="quantity"][value="' + Number(wanted) + '"]');
      if (wantedInput) wantedInput.checked = true;
      this.sync();
    }
    disconnectedCallback() {
      this.unsubscribe && this.unsubscribe();
    }
    get selected() {
      return this.querySelector('input[name="quantity"]:checked');
    }
    get quantity() {
      return this.selected ? Number(this.selected.value) : 1;
    }
    /* Live totals from each unit's real price (mix-and-match units can differ, e.g. memory card sizes).
       Offer formats match the Liquid: 15 (percent), amount:23.99, half:50, free:1. */
    recalc() {
      const mapEl = this.querySelector('[data-sp-variant-map]');
      if (!mapEl) return;
      let map;
      try {
        map = JSON.parse(mapEl.textContent);
      } catch (e) {
        return;
      }
      const amount = (cents) => (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const money = (cents, format) => (format || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, amount(cents));
      const unit = (i) => {
        const slot = this.querySelector('[data-sp-slot="' + i + '"]');
        if (!slot || !slot.querySelector('[data-sp-opt]')) return null;
        const values = slotValues(slot);
        return map.find((v) => v.options.every((value, index) => value === values[index])) || null;
      };
      this.querySelectorAll('input[name="quantity"][data-offer]').forEach((input) => {
        const qty = Number(input.value);
        const prices = [];
        for (let i = 1; i <= qty; i++) {
          const v = unit(i);
          if (!v) return;
          prices.push(v.price);
        }
        prices.sort((a, b) => a - b);
        const regular = prices.reduce((sum, p) => sum + p, 0);
        const offer = input.dataset.offer || '';
        let saving = 0;
        if (offer.indexOf('amount:') === 0) saving = Math.min(Math.round(Number(offer.slice(7)) * 100), prices[0]);
        else if (offer.indexOf('half:') === 0) saving = Math.round((prices[0] * Number(offer.slice(5))) / 100);
        else if (offer.indexOf('free:') === 0) saving = prices.slice(0, Number(offer.slice(5))).reduce((sum, p) => sum + p, 0);
        else if (Number(offer) > 0) saving = regular - Math.round((regular * (100 - Number(offer))) / 100);
        const option = input.closest('.sp-set-option');
        if (!option) return;
        const total = option.querySelector('[data-sp-set-price]');
        const was = option.querySelector('[data-sp-regular]');
        const save = option.querySelector('[data-sp-save]');
        if (total) total.textContent = money(regular - saving, this.dataset.moneyFormat);
        if (was) was.textContent = money(regular, this.dataset.moneyFormat);
        if (save) save.textContent = money(saving, this.dataset.moneyFormatShort);
      });
    }
    sync() {
      this.recalc();
      const input = this.selected;
      if (!input) return;
      const qty = this.quantity;
      if (this.slotsWrap) {
        // Products with 2+ options (colour + card) always use the per-unit choosers, even for 1 item.
        const alwaysUnits = Boolean(this.slotsWrap.querySelector('[data-sp-variant-map]'));
        this.slotsWrap.hidden = !alwaysUnits && qty < 2;
        // The per-unit choosers replace the main style picker (it stays in the DOM and follows unit #1).
        const info = this.closest('.product__info-container');
        const mainPicker = info && info.querySelector('variant-selects');
        if (mainPicker) mainPicker.hidden = alwaysUnits || qty >= 2;
        this.slotsWrap.querySelectorAll('[data-sp-slot]').forEach((slot) => {
          slot.hidden = Number(slot.dataset.spSlot) > qty;
        });
      }
      const label = input.closest('.sp-set-option')?.querySelector('[data-sp-set-price]');
      document.dispatchEvent(
        new CustomEvent('sp:set-change', {
          detail: { quantity: qty, priceText: label ? label.textContent.trim() : '', title: input.dataset.title || '' },
        })
      );
    }
    /* Unit #1 drives the product's main selection (price, photos, Add to cart) via the hidden main picker. */
    pushToMainPicker(options) {
      const info = this.closest('.product__info-container');
      const picker = info && info.querySelector('variant-selects');
      if (!picker) return;
      let changed = null;
      picker.querySelectorAll('fieldset, .product-form__input').forEach((group, index) => {
        const value = options[index];
        if (value === undefined) return;
        const radio = [...group.querySelectorAll('input[type="radio"]')].find((r) => r.value === value);
        if (radio && !radio.checked) {
          radio.checked = true;
          changed = radio;
        }
        const select = group.querySelector('select');
        if (select && select.value !== value) {
          select.value = value;
          changed = select;
        }
      });
      if (changed) changed.dispatchEvent(new Event('change', { bubbles: true }));
    }
    /* Cart lines for a mixed-style multi-buy, or null when the normal single-variant add should run. */
    multiItems() {
      const qty = this.quantity;
      if (!this.slotsWrap) return null;
      // 2+ option products (colour + card) always add exactly what the choosers show, even for 1 item.
      const alwaysUnits = Boolean(this.slotsWrap.querySelector('[data-sp-variant-map]'));
      if (qty < 2 && !alwaysUnits) return null;
      const counts = new Map();
      let map = null;
      const mapEl = this.slotsWrap.querySelector('[data-sp-variant-map]');
      if (mapEl) {
        try {
          map = JSON.parse(mapEl.textContent);
        } catch (e) {
          return null;
        }
      }
      for (let i = 1; i <= qty; i++) {
        const slot = this.slotsWrap.querySelector('[data-sp-slot="' + i + '"]');
        if (!slot) return null;
        let id = null;
        if (slot.querySelector('[data-sp-opt]') && map) {
          const values = slotValues(slot);
          const match = map.find((v) => v.options.every((value, index) => value === values[index]));
          if (!match || !match.available) return null;
          id = String(match.id);
        } else {
          const checked = slot.querySelector('input:checked');
          if (!checked) return null;
          id = checked.value;
        }
        counts.set(id, (counts.get(id) || 0) + 1);
      }
      return [...counts].map(([id, quantity]) => ({ id: Number(id), quantity }));
    }
  }
  if (!customElements.get('sp-set-picker')) customElements.define('sp-set-picker', SpSetPicker);

  /* ---------- Mixed-style multi-buy on the product page ----------
     Runs in the capture phase, before Dawn's product-form handler, and only when the Buy 2/3 picker
     has per-unit styles. Otherwise Dawn's normal add-to-cart runs untouched. */
  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.id) return;
      const picker = document.querySelector('sp-set-picker[data-form-id="' + form.id + '"]');
      const items = picker && picker.multiItems();
      if (!items) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const btn = form.querySelector('[type="submit"]');
      if (btn && btn.getAttribute('aria-disabled') === 'true') return;
      const spinner = form.querySelector('.loading__spinner');
      const errorWrap = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
      if (errorWrap) errorWrap.hidden = true;
      btn && (btn.setAttribute('aria-disabled', 'true'), btn.classList.add('loading'));
      spinner && spinner.classList.remove('hidden');
      try {
        await addLines(items, btn);
      } catch (err) {
        if (errorWrap) {
          const msg = errorWrap.querySelector('.product-form__error-message');
          if (msg) msg.textContent = err.message;
          errorWrap.hidden = false;
        }
      } finally {
        btn && (btn.removeAttribute('aria-disabled'), btn.classList.remove('loading'));
        spinner && spinner.classList.add('hidden');
      }
    },
    true
  );

  /* ---------- Sticky add to cart (appears once the main ATC scrolls out of view) ---------- */
  class SpStickyAtc extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.formId;
      this.target = document.getElementById(this.dataset.watch);
      this.button = this.querySelector('button');
      this.priceEl = this.querySelector('[data-sp-sticky-price]');
      this.optionEl = this.querySelector('[data-sp-sticky-option]');
      if (!this.target || !this.button) return;

      this.button.addEventListener('click', () => {
        const form = document.getElementById(this.formId);
        if (!form) return;
        if (form.requestSubmit) form.requestSubmit();
        else form.querySelector('[type="submit"]')?.click();
      });

      this.observer = new IntersectionObserver(
        ([entry]) => {
          const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', past);
          document.body.classList.toggle('sp-sticky-open', past);
          this.setAttribute('aria-hidden', past ? 'false' : 'true');
          this.button.tabIndex = past ? 0 : -1;
        },
        { threshold: 0 }
      );
      this.observer.observe(this.target);

      this.onSet = (e) => {
        if (this.priceEl && e.detail.priceText) this.priceEl.textContent = e.detail.priceText;
        if (this.optionEl && e.detail.title) this.optionEl.textContent = e.detail.title;
      };
      document.addEventListener('sp:set-change', this.onSet);

      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event.data && event.data.variant;
          if (!variant) {
            this.button.disabled = true;
            return;
          }
          if (this.optionEl) this.optionEl.textContent = variant.title === 'Default Title' ? '' : variant.title;
          const html = event.data.html;
          const price = html && html.querySelector(`#price-${this.dataset.section} .price-item--last`);
          if (this.priceEl && price) this.priceEl.textContent = price.textContent.trim();
          this.button.disabled = !variant.available;
          const label = this.button.querySelector('.sp-btn__label');
          if (label) label.textContent = variant.available ? window.variantStrings.addToCart : window.variantStrings.soldOut;
        });
      }
    }
    disconnectedCallback() {
      this.observer && this.observer.disconnect();
      this.unsubscribe && this.unsubscribe();
      this.onSet && document.removeEventListener('sp:set-change', this.onSet);
    }
  }
  if (!customElements.get('sp-sticky-atc')) customElements.define('sp-sticky-atc', SpStickyAtc);

  /* ---------- Horizontal scroller with prev/next buttons ---------- */
  class SpScroller extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.sp-scroller');
      this.prev = this.querySelector('[data-sp-prev]');
      this.next = this.querySelector('[data-sp-next]');
      if (!this.track) return;
      const step = () => (this.track.firstElementChild ? this.track.firstElementChild.getBoundingClientRect().width + 16 : 300);
      this.prev && this.prev.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
      this.next && this.next.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
      this.update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      };
      this.track.addEventListener('scroll', this.update, { passive: true });
      this.update();
    }
  }
  if (!customElements.get('sp-scroller')) customElements.define('sp-scroller', SpScroller);

  /* ---------- Lazy video: sources load only near the viewport; autoplay muted while visible ---------- */
  class SpVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('[data-sp-video-toggle]');
      if (!this.video) return;
      this.autoplay = this.dataset.autoplay === 'true' && !reduceMotion;
      this.userPaused = false;

      this.toggle &&
        this.toggle.addEventListener('click', () => {
          this.load();
          if (this.video.paused) {
            this.userPaused = false;
            this.video.play().catch(() => {});
          } else {
            this.userPaused = true;
            this.video.pause();
          }
        });
      this.video.addEventListener('play', () => this.sync());
      this.video.addEventListener('pause', () => this.sync());

      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            this.load();
            if (this.autoplay && !this.userPaused) this.video.play().catch(() => {});
          } else if (!this.video.paused) {
            this.video.pause();
          }
        },
        { rootMargin: '200px 0px', threshold: 0.35 }
      );
      this.observer.observe(this);
      this.sync();
    }
    load() {
      if (this.loaded) return;
      this.loaded = true;
      this.video.querySelectorAll('source[data-src]').forEach((s) => (s.src = s.dataset.src));
      this.video.load();
    }
    sync() {
      if (!this.toggle) return;
      const playing = !this.video.paused;
      this.toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      this.toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
      this.toggle.querySelector('[data-icon-play]')?.toggleAttribute('hidden', playing);
      this.toggle.querySelector('[data-icon-pause]')?.toggleAttribute('hidden', !playing);
    }
    disconnectedCallback() {
      this.observer && this.observer.disconnect();
    }
  }
  if (!customElements.get('sp-video')) customElements.define('sp-video', SpVideo);

  /* ---------- Click-to-load YouTube/Vimeo (no third-party JS until the viewer presses play) ---------- */
  class SpVideoEmbed extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('button');
      btn && btn.addEventListener('click', () => {
        const iframe = document.createElement('iframe');
        iframe.src = this.dataset.src;
        iframe.title = this.dataset.title || 'Video';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        this.replaceChildren(iframe);
        iframe.focus();
      }, { once: true });
    }
  }
  if (!customElements.get('sp-video-embed')) customElements.define('sp-video-embed', SpVideoEmbed);

  /* ---------- Subtle reveal on scroll ---------- */
  if (!reduceMotion && 'IntersectionObserver' in window) {
    document.documentElement.classList.add('sp-js');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    const observe = (root) => root.querySelectorAll('.sp-reveal:not(.is-in)').forEach((el) => io.observe(el));
    document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => observe(document)) : observe(document);
    document.addEventListener('shopify:section:load', (e) => observe(e.target));
  }
})();
